import { supabase } from '../lib/supabase';

// Get all venues with optional filters
export const getAllVenues = async (filters = {}) => {
  try {
    let query = supabase?.from('venues')?.select('*')?.eq('is_active', true)?.order('created_at', { ascending: false });

    if (filters?.search) {
      query = query?.or(`name.ilike.%${filters?.search}%,city.ilike.%${filters?.search}%,address_line_1.ilike.%${filters?.search}%`);
    }

    if (filters?.city) {
      query = query?.eq('city', filters?.city);
    }

    if (filters?.capacity_min) {
      query = query?.gte('capacity', filters?.capacity_min);
    }

    if (filters?.capacity_max) {
      query = query?.lte('capacity', filters?.capacity_max);
    }

    const { data, error } = await query;

    if (error) {
      throw new Error(error.message);
    }

    return data || [];
  } catch (error) {
    throw new Error(`Failed to fetch venues: ${error?.message}`);
  }
};

// Get venue by ID
export const getVenueById = async (id) => {
  try {
    const { data, error } = await supabase?.from('venues')?.select('*')?.eq('id', id)?.single();

    if (error) {
      throw new Error(error.message);
    }

    return data;
  } catch (error) {
    throw new Error(`Failed to fetch venue: ${error?.message}`);
  }
};

// Create new venue
export const createVenue = async (venueData) => {
  try {
    const { data, error } = await supabase?.from('venues')?.insert([{
        name: venueData?.name,
        address_line_1: venueData?.address_line_1,
        address_line_2: venueData?.address_line_2,
        city: venueData?.city,
        state: venueData?.state,
        postal_code: venueData?.postal_code,
        country: venueData?.country || 'IN',
        capacity: venueData?.capacity,
        description: venueData?.description,
        contact_phone: venueData?.contact_phone,
        contact_email: venueData?.contact_email,
        amenities: venueData?.amenities || []
      }])?.select()?.single();

    if (error) {
      throw new Error(error.message);
    }

    return data;
  } catch (error) {
    throw new Error(`Failed to create venue: ${error?.message}`);
  }
};

// Update venue
export const updateVenue = async (id, venueData) => {
  try {
    const { data, error } = await supabase?.from('venues')?.update({
        name: venueData?.name,
        address_line_1: venueData?.address_line_1,
        address_line_2: venueData?.address_line_2,
        city: venueData?.city,
        state: venueData?.state,
        postal_code: venueData?.postal_code,
        country: venueData?.country,
        capacity: venueData?.capacity,
        description: venueData?.description,
        contact_phone: venueData?.contact_phone,
        contact_email: venueData?.contact_email,
        amenities: venueData?.amenities
      })?.eq('id', id)?.select()?.single();

    if (error) {
      throw new Error(error.message);
    }

    return data;
  } catch (error) {
    throw new Error(`Failed to update venue: ${error?.message}`);
  }
};

// Delete venue (soft delete by setting is_active to false)
export const deleteVenue = async (id) => {
  try {
    const { data, error } = await supabase?.from('venues')?.update({ is_active: false })?.eq('id', id)?.select()?.single();

    if (error) {
      throw new Error(error.message);
    }

    return data;
  } catch (error) {
    throw new Error(`Failed to delete venue: ${error?.message}`);
  }
};

// Check for duplicate venues
export const checkDuplicateVenue = async (name, addressLine1, city) => {
  try {
    const { data, error } = await supabase?.from('venues')?.select('id, name, address_line_1, city')?.eq('is_active', true)?.or(`and(name.ilike.%${name}%,city.ilike.%${city}%),and(address_line_1.ilike.%${addressLine1}%,city.ilike.%${city}%)`);

    if (error) {
      throw new Error(error.message);
    }

    return data || [];
  } catch (error) {
    throw new Error(`Failed to check duplicate venues: ${error?.message}`);
  }
};

// Get venue statistics
export const getVenueStats = async () => {
  try {
    const { data, error } = await supabase?.from('venues')?.select('id, capacity, city')?.eq('is_active', true);

    if (error) {
      throw new Error(error.message);
    }

    const stats = {
      total: data?.length || 0,
      byCity: {},
      totalCapacity: 0,
      averageCapacity: 0
    };

    data?.forEach(venue => {
      // City distribution
      if (venue?.city) {
        stats.byCity[venue.city] = (stats?.byCity?.[venue?.city] || 0) + 1;
      }
      
      // Capacity calculations
      if (venue?.capacity) {
        stats.totalCapacity += venue?.capacity;
      }
    });

    stats.averageCapacity = stats?.total > 0 ? Math.round(stats?.totalCapacity / stats?.total) : 0;

    return stats;
  } catch (error) {
    throw new Error(`Failed to get venue statistics: ${error?.message}`);
  }
};

export default {
  getAllVenues,
  getVenueById,
  createVenue,
  updateVenue,
  deleteVenue,
  checkDuplicateVenue,
  getVenueStats
};